import { useLayoutEffect } from 'react'
import {
  NAV_CLEARANCE_CSS,
  NAV_GAP_PX,
  NAV_INLINE_END_CLEARANCE_CSS,
  NAV_TRIGGER_INLINE_SIZE,
} from './experienceNavSurface.styles'

/**
 * Publishes what the navigation controls occupy, as custom properties on
 * :root, so content that would collide can clear them with a single
 * declaration. Only this component knows whether anything actually rendered -
 * that depends on mode, registration and Home visibility - which is why the
 * measurements are published rather than re-derived by each consumer.
 *
 * All of them are removed on unmount (→ each consumer's own 0px fallback), so
 * screens where ConditionalHeader never mounts the controls get no phantom
 * padding.
 */
export default function useExperienceNavCssVars({ isStation, hasControls, occupiesTopCorner, occupiesTopEndCorner }) {
  // 0px, not a measured height: these controls are position:fixed and displace
  // no layout, so pages sizing with calc(100vh - var(--station-menu-height, …))
  // get the whole viewport.
  useLayoutEffect(() => {
    if (!isStation) return undefined
    document.documentElement.style.setProperty('--station-menu-height', '0px')
    return () => {
      document.documentElement.style.removeProperty('--station-menu-height')
    }
  }, [isStation])

  // See NAV_CLEARANCE_CSS: the vertical room taken at the top of the content
  // area, for content anchored there.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--experience-nav-clearance',
      occupiesTopCorner ? NAV_CLEARANCE_CSS : '0px'
    )
    return () => {
      document.documentElement.style.removeProperty('--experience-nav-clearance')
    }
  }, [occupiesTopCorner])

  // See NAV_INLINE_END_CLEARANCE_CSS: the inline room the stepper takes on
  // the top-END corner, for content that owns that corner.
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--experience-nav-inline-end-clearance',
      occupiesTopEndCorner ? NAV_INLINE_END_CLEARANCE_CSS : '0px'
    )
    return () => {
      document.documentElement.style.removeProperty('--experience-nav-inline-end-clearance')
    }
  }, [occupiesTopEndCorner])

  // Everything to clear on the inline-start edge - the controls' footprint
  // plus the gap after it - as --experience-nav-inline-clearance, for
  // consumers whose box starts at the content-area origin (the tab-bar
  // gutters). Only STATION's edge tab, a known width flush against the edge,
  // takes inline room; the corner trigger takes it only at the very top, which
  // --experience-nav-clearance already reserves, so it publishes 0px here.
  // (Content that shares the corner with the trigger, heritage's country band,
  // reads useExperienceNavOccupiesTopCorner and sizes its own reserve.)
  useLayoutEffect(() => {
    const isEdgeTab = hasControls && isStation
    document.documentElement.style.setProperty(
      '--experience-nav-inline-clearance',
      isEdgeTab ? `calc(${NAV_TRIGGER_INLINE_SIZE} + ${NAV_GAP_PX}px)` : '0px'
    )
    return () => {
      document.documentElement.style.removeProperty('--experience-nav-inline-clearance')
    }
  }, [hasControls, isStation])
}
